import React, { useState } from 'react';
import { ShieldCheck, AlertTriangle, FileSearch, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Small score chip + collapsible breakdown shown on a creator's OWN product
 * card in their dashboard. Surfaces the auto-vetting result back to them
 * so they can iterate on weak listings without admin involvement.
 *
 * Decision colors match AdminVettingLog.jsx so admin and creator see the
 * same visual language. Renders nothing if `vetting` is missing (legacy
 * products predate the pipeline).
 *
 * Props
 *   vetting   The product.vetting_results object from the backend
 */
export const VetScoreBadge = ({ vetting }) => {
  const [open, setOpen] = useState(false);
  if (!vetting) return null;

  const score = vetting?.listing_review?.score ?? null;
  const decision = vetting?.final_decision || 'pending_review';
  const concerns = vetting?.listing_review?.concerns || [];

  const cfg = {
    auto_approve:   { label: 'Verified',    Icon: ShieldCheck,    text: 'text-[#00FF94]', bg: 'bg-[#00FF94]/10', border: 'border-[#00FF94]/30' },
    pending_review: { label: 'Needs review', Icon: FileSearch,    text: 'text-yellow-300', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
    auto_reject:    { label: 'Rejected',    Icon: AlertTriangle, text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  }[decision] || { label: 'Vetting', Icon: FileSearch, text: 'text-neutral-300', bg: 'bg-white/5', border: 'border-white/10' };

  const showExpand = decision !== 'auto_approve' && (concerns.length > 0 || vetting?.reason);

  return (
    <div className="mb-3" data-testid="vet-score-badge">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (showExpand) setOpen(!open); }}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold ${cfg.bg} ${cfg.text} ${cfg.border} ${showExpand ? 'cursor-pointer hover:brightness-110' : 'cursor-default'} transition`}
        title={vetting?.reason || 'Auto-vet result for this product'}
        aria-expanded={open}
      >
        <cfg.Icon className="w-3 h-3" />
        <span>{cfg.label}</span>
        {typeof score === 'number' && (
          <span className="opacity-80">· {score}/100</span>
        )}
        {showExpand && (open ? <ChevronUp className="w-3 h-3 ml-0.5" /> : <ChevronDown className="w-3 h-3 ml-0.5" />)}
      </button>
      {showExpand && open && (
        <div className={`mt-2 p-3 rounded-lg ${cfg.bg} ${cfg.border} border text-[11px] ${cfg.text}`}>
          {vetting?.reason && (
            <p className="mb-2 leading-relaxed font-semibold">{vetting.reason}</p>
          )}
          {concerns.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-wider opacity-70 font-bold mb-1">How to improve</p>
              <ul className="space-y-0.5 list-disc list-inside opacity-90">
                {concerns.slice(0, 5).map((c, i) => (
                  <li key={`${c}-${i}`}>{c}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VetScoreBadge;
